import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { useForm } from 'react-hook-form'
import { usePublicContent } from '../hooks/usePublicContent'
import { ContactContent } from '../types/content'
import '../types/recaptcha'
import { 
  FiInstagram,
  FiTwitter,
  FiLinkedin,
  FiMusic,
  FiVideo,
  FiSend
} from 'react-icons/fi'

interface FormData {
  name: string
  email: string
  phone?: string
  serviceType: string
  message: string
}

const Contact: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const { content } = usePublicContent('contact')
  const contactData = content?.data as ContactContent

  const { register, handleSubmit, formState: { errors }, reset } = useForm<FormData>()

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true)
    
    // Prepare form data for the Cloud Function
    const formData = {
      name: data.name,
      email: data.email,
      subject: `${data.serviceType} - Contact Form`,
      message: data.message,
      phone: data.phone || '',
      serviceType: data.serviceType,
      recaptchaToken: ''
    }
    
    try {
      // Get reCAPTCHA token
      const recaptchaToken = await getRecaptchaToken()
      formData.recaptchaToken = recaptchaToken

      // Call the Cloud Function
      const response = await fetch('https://submitcontactform-kvyoqs4s6a-nw.a.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      })

      const result = await response.json()

      if (response.ok && result.success) {
        setSubmitSuccess(true)
        reset()
        setTimeout(() => setSubmitSuccess(false), 5000)
      } else {
        console.error('Server error response:', result)
        console.error('Validation details:', result.details)
        const errorMsg = result.details ? 
          `${result.error}: ${result.details.join(', ')}` : 
          (result.error || 'Failed to send message')
        throw new Error(errorMsg)
      }
    } catch (error) {
      console.error('Form submission error:', error)
      console.error('Form data sent:', formData)
      const errorMessage = error instanceof Error ? error.message : 'Failed to send message. Please try again later.'
      alert(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  // reCAPTCHA token generation
  const getRecaptchaToken = (): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (typeof window !== 'undefined' && window.grecaptcha) {
        window.grecaptcha.ready(() => {
          window.grecaptcha.execute('6Lc0iuwrAAAAANGfCVKYnTPeynpFgcJ70BGBddpt', { action: 'contact_form' })
            .then((token: string) => resolve(token))
            .catch(reject)
        })
      } else {
        // Fallback for development or if reCAPTCHA fails
        resolve('dev-token')
      }
    })
  }


  // Build social links array from CMS data, only including links that are present
  const socialLinks = []
  if (contactData?.socialLinks?.instagram) {
    socialLinks.push({ icon: FiInstagram, href: contactData.socialLinks.instagram, label: 'Instagram' })
  }
  if (contactData?.socialLinks?.twitter) {
    socialLinks.push({ icon: FiTwitter, href: contactData.socialLinks.twitter, label: 'Twitter' })
  }
  if (contactData?.socialLinks?.linkedin) {
    socialLinks.push({ icon: FiLinkedin, href: contactData.socialLinks.linkedin, label: 'LinkedIn' })
  }
  if (contactData?.socialLinks?.spotify) {
    socialLinks.push({ icon: FiMusic, href: contactData.socialLinks.spotify, label: 'Spotify' })
  }
  if (contactData?.socialLinks?.tiktok) {
    socialLinks.push({ icon: FiVideo, href: contactData.socialLinks.tiktok, label: 'TikTok' })
  }

  // Use service types from CMS, with fallback to default options
  const serviceTypes = contactData?.serviceTypes || [
    'Wedding Music',
    'Corporate Event',
    'Bar/Restaurant Booking',
    'Private Party',
    'Venue Partnership',
    'Music Consultation',
    'Other'
  ]

  return (
    <section 
      id="contact" 
      className="section-padding relative overflow-hidden"
      style={{ 
        background: `
          radial-gradient(circle at 20% 80%, rgba(97, 131, 158, 0.1) 0%, transparent 50%),
          conic-gradient(from 180deg at 50% 50%, rgba(15, 60, 75, 0.05) 0deg, rgba(255, 255, 255, 0.1) 60deg, rgba(97, 131, 158, 0.08) 120deg, rgba(15, 60, 75, 0.05) 180deg, rgba(255, 255, 255, 0.1) 240deg, rgba(97, 131, 158, 0.08) 300deg, rgba(15, 60, 75, 0.05) 360deg),
          #F2F2F2
        `
      }}
    >
      <div className="container-custom">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-bold text-neutral-800 mb-6" style={{ fontSize: 'clamp(32px, 4vw, 48px)' }}>
            Book Live Music for <span className="gradient-text">Your Event</span>
          </h2>
          <p className="body-longform text-neutral-600 max-w-3xl mx-auto px-4">
            Ready to add live music to your event? Get in touch with us to discuss your needs, 
            browse available musicians, or book the perfect performers for your occasion.
          </p>
        </motion.div>

        <div className="max-w-2xl mx-auto">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="card"
          >
            <h3 className="font-bold text-neutral-800 mb-4 sm:mb-6 text-center" style={{ fontSize: 'clamp(22px, 3vw, 32px)' }}>
              Send us a message
            </h3>

            {submitSuccess && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700"
              >
                Thank you for your message! We'll get back to you within 24 hours.
              </motion.div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    {...register('name', { required: 'Name is required' })}
                    className="w-full px-4 py-3 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-200 min-h-[44px]"
                    placeholder="Your full name"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    {...register('email', { 
                      required: 'Email is required',
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    className="w-full px-4 py-3 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-200 min-h-[44px]"
                    placeholder="your@email.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    {...register('phone')}
                    className="w-full px-4 py-3 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-200 min-h-[44px]"
                    placeholder="+44 7700 900000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Service Type *
                  </label>
                  <select
                    {...register('serviceType', { required: 'Please select a service type' })}
                    className="w-full px-4 py-3 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-200 min-h-[44px]"
                  >
                    <option value="">Select a service</option>
                    {serviceTypes.map((service) => (
                      <option key={service} value={service}>
                        {service}
                      </option>
                    ))}
                  </select>
                  {errors.serviceType && (
                    <p className="mt-1 text-sm text-red-600">{errors.serviceType.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Message *
                </label>
                <textarea
                  {...register('message', { required: 'Message is required' })}
                  rows={5}
                  className="w-full px-4 py-3 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-200 resize-vertical"
                  placeholder="Tell us about your project, event, or how we can help you..."
                />
                {errors.message && (
                  <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>
                )}
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="loading-spinner"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <FiSend />
                    <span>Send Message</span>
                  </>
                )}
              </motion.button>

              {/* reCAPTCHA Notice - Required by Google when hiding badge */}
              <p className="text-xs text-neutral-500 text-center mt-3">
                This site is protected by reCAPTCHA and the Google{' '}
                <a 
                  href="https://policies.google.com/privacy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-secondary hover:underline"
                >
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a 
                  href="https://policies.google.com/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-secondary hover:underline"
                >
                  Terms of Service
                </a>{' '}
                apply.
              </p>
            </form>
          </motion.div>

          {/* Social Links */}
          {socialLinks.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-8 text-center"
            >
              <h4 className="font-semibold text-neutral-700 mb-4 text-sm">
                Follow us on social media
              </h4>
              <div className="flex justify-center gap-4">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.href}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="p-3 bg-neutral-100 text-primary rounded-xl hover:bg-neutral-200 transition-colors duration-200"
                    aria-label={social.label}
                  >
                    <social.icon size={20} />
                  </motion.a>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  )
}

export default Contact
